import { NavLink, Navigate, Route, Routes, useOutletContext } from 'react-router-dom';
import type { Program } from '@/lib/types';
import { cn } from '@/lib/utils';
import { PageHeader } from '@/components/ui/misc';
import { People } from './People';
import { ProgramSettings } from './ProgramSettings';
import { Seats } from './Seats';
import { Facts } from './Facts';
import { AuditLog } from './AuditLog';
import { Notifications } from './Notifications';
import { DataTab } from './DataTab';
import { AccessReview } from './AccessReview';
import { AiTab } from './AiTab';
import { RecentlyDeleted } from './RecentlyDeleted';
import { useMe } from '@/lib/auth';

const TABS = [
  { path: 'people', label: 'People' },
  { path: 'program', label: 'Program' },
  { path: 'committee', label: 'Steering Committee' },
  { path: 'facts', label: 'Program facts' },
  { path: 'notifications', label: 'Notifications' },
  { path: 'audit', label: 'Audit log' },
  { path: 'access', label: 'Access review' },
  { path: 'ai', label: 'Assistant' },
  { path: 'deleted', label: 'Recently deleted' },
  { path: 'data', label: 'Export and purge' },
];

export function Admin() {
  const program = useOutletContext<Program>();
  const { data: me } = useMe();
  if (!program.capabilities.includes('admin.manage')) return <Navigate to={`/p/${program.id}/tracker`} replace />;
  // Absolute paths: relative links inside the admin/* splat resolve against the current sub-tab.
  const root = `/p/${program.id}/admin`;

  return (
    <div className="mx-auto max-w-[1200px] px-6 py-6">
      <PageHeader title="Admin" description={`${program.clientName} ${program.name}`} />
      <div className="mb-5 flex gap-1 overflow-x-auto border-b border-border">
        {TABS.map((t) => (
          <NavLink
            key={t.path}
            to={`${root}/${t.path}`}
            className={({ isActive }) =>
              cn(
                '-mb-px whitespace-nowrap border-b-2 px-3 pb-2.5 pt-1 text-sm transition-colors',
                isActive ? 'border-brand font-medium text-text' : 'border-transparent text-muted hover:text-text',
              )
            }
          >
            {t.label}
          </NavLink>
        ))}
      </div>
      <Routes>
        <Route index element={<Navigate to={`${root}/people`} replace />} />
        <Route path="people" element={<People program={program} />} />
        <Route path="program" element={<ProgramSettings program={program} />} />
        <Route path="committee" element={<Seats program={program} />} />
        <Route path="facts" element={<Facts program={program} />} />
        <Route path="notifications" element={<Notifications program={program} />} />
        <Route path="audit" element={<AuditLog program={program} />} />
        <Route path="access" element={<AccessReview program={program} meId={me?.user.id} />} />
        <Route path="ai" element={<AiTab program={program} />} />
        <Route path="deleted" element={<RecentlyDeleted program={program} />} />
        <Route path="data" element={<DataTab program={program} />} />
        <Route path="*" element={<Navigate to={`${root}/people`} replace />} />
      </Routes>
    </div>
  );
}
